import { Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { delay, filter, map, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  CollectionAction,
  CollectionNotificationWsMessage,
  CollectionWsMessage,
  ServerToClientMessageBO,
  TickerCommandPayload,
} from '../dto/tickers-dtos';
import { EncodingUtils } from '../../../../common/encoding-utils';
import { SignInService } from '../../../../common/services/sign-in/sign-in.service';
import { LottoUser } from '../../sign-in/user.model';

@Injectable({
  providedIn: 'root',
})
export class BlastV2Service {
  public static COLLECTION_WS_CMD: string = 'collection.action';

  public static NOTIFICATION_WS_CMD: string = 'collection.notification';

  blastWebSocket: WebSocketSubject<any>;

  private endpoint: string;

  private blastConnected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private isConnected: boolean = false;

  private cacheMap: Map<string, Map<number, any>> = new Map<string, Map<number, any>>();

  private allElementSubject: Map<string, Subject<any>> = new Map<string, Subject<any[]>>();

  private singleElementSubject: Map<string, Subject<any>> = new Map<string, Subject<any>>();

  private forceRefreshSubject: Subject<string> = new BehaviorSubject<string>(null);

  private blastWebSocketSubscription: Subscription;

  private messagesSubject: Subject<CollectionNotificationWsMessage> =
    new BehaviorSubject<CollectionNotificationWsMessage>(null);

  private jwtHelperService = new JwtHelperService();

  constructor(
    private signInService: SignInService,
    private toastrService: ToastrService
  ) {
    this.jwtHelperService = new JwtHelperService();
    signInService.initiatedUser.pipe(filter((d) => !!d)).subscribe((_) => {
      if (!this.isConnected) {
        this.connect(this.signInService.getCurrentUser());
      }
    });

    this.blastConnected.subscribe((v) => {
      this.isConnected = v;
    });
  }

  connect(user: LottoUser) {
    const endpoint =
      (location.protocol.includes('https') ? 'wss' : 'ws') + '://' + window.location.host + '/admin/blast';
    const token = user.getToken();
    this.endpoint = `${endpoint}?token=${token}`;
    this.blastWebSocket = webSocket({
      url: this.endpoint,
      closeObserver: {
        next: (d) => {
          this.unsubscribeFromWs(d);

          if (this.isActive(token)) {
            // subscribe to WS if old token is active
            this.subscribeToWs(true);
          } else if (this.isActive(user.getToken())) {
            // reconnect with new token is old one expired
            this.reconnect(user);
          }
        },
      },
      openObserver: {
        next: () => {
          this.blastConnected.next(true);
        },
      },
    });
    this.subscribeToWs(false);
  }

  private unsubscribeFromWs(d: CloseEvent) {
    if (this.blastWebSocketSubscription && !this.blastWebSocketSubscription.closed) {
      this.blastWebSocketSubscription.unsubscribe();
    }
    this.blastConnected.next(false);
  }

  private reconnect(user: LottoUser) {
    this.blastWebSocket.unsubscribe();
    this.connect(user);
  }

  private isActive(token: string) {
    return !this.jwtHelperService.isTokenExpired(token);
  }

  public getRefreshObservable(): Observable<string> {
    return this.forceRefreshSubject.asObservable();
  }

  public getCollectionObservable(collection: string): Observable<any[]> {
    return this.getOrCreateAllElementSubject(collection).asObservable();
  }

  public getCollection(collection: string) {
    const values = this.cacheMap.get(collection)?.values();
    return values ? Array.from(values) : null;
  }

  public getSingleValueSubject(collection: string): Subject<CollectionWsMessage<any>> {
    return this.getOrCreateSingleElementSubject(collection);
  }

  public sendMessage(command: TickerCommandPayload<any>) {
    this.blastWebSocket.next(EncodingUtils.encodeRequest(command));
  }

  private handleNotificationWsMessage(dataFromServer: ServerToClientMessageBO<any>) {
    const messageData = dataFromServer.data as CollectionNotificationWsMessage;
    this.messagesSubject.next(messageData);
    if (messageData.timeout !== null) {
      this.toastrService.show(messageData.message, '', { timeOut: messageData.timeout }, `toast-${messageData.type}`);
    } else {
      this.toastrService.show(messageData.message, '', {}, `toast-${messageData.type}`);
    }
  }

  public getMessagesObservable(): Observable<CollectionNotificationWsMessage> {
    return this.messagesSubject.asObservable();
  }

  public connectedObservable(): Observable<boolean> {
    return this.blastConnected.asObservable();
  }

  private subscribeToWs(reconnect: boolean) {
    this.blastWebSocketSubscription = of({})
      .pipe(
        delay(reconnect ? 3000 : 0),
        switchMap((_) => this.blastWebSocket),
        map((dataFromServer) => EncodingUtils.decodeResponse(dataFromServer))
      )
      .subscribe(
        (dataFromServer) => {
          this.handleMessage(dataFromServer);
        },
        (e) => {
          console.log('[Blast] error', e);
        }
      );
  }

  private handleMessage(dataFromServer: ServerToClientMessageBO<any>) {
    switch (dataFromServer.cmd) {
      case BlastV2Service.COLLECTION_WS_CMD:
        this.handleCollectionWsMessage(dataFromServer);
        break;
      case BlastV2Service.NOTIFICATION_WS_CMD:
        this.handleNotificationWsMessage(dataFromServer);
        break;
    }
  }

  private handleCollectionWsMessage(dataFromServer: ServerToClientMessageBO<any>) {
    const messageData = dataFromServer.data as CollectionWsMessage<any>;

    switch (messageData.action) {
      case CollectionAction.DROP_ALL:
        Object.keys(messageData.data).forEach((key) => {
          const collectionContent: any[] = messageData.data[key] || [];
          this.handlePopulateCollectionMessage(key, collectionContent);
        });
        break;
      case CollectionAction.REMOVE:
        this.getOrCreateCollectionMap(messageData.collection).delete(messageData.data.id);
        this.getOrCreateSingleElementSubject(messageData.collection).next(messageData);
        this.updateAllElementSubject(messageData.collection);
        break;
      case CollectionAction.ADD:
        this.getOrCreateCollectionMap(messageData.collection).set(messageData.data.id, messageData.data);
        this.getOrCreateSingleElementSubject(messageData.collection).next(messageData);
        this.updateAllElementSubject(messageData.collection);
        break;
      case CollectionAction.UPDATE:
        this.getOrCreateCollectionMap(messageData.collection).set(messageData.data.id, messageData.data);
        this.getOrCreateSingleElementSubject(messageData.collection).next(messageData);
        this.updateAllElementSubject(messageData.collection);
        break;
      case CollectionAction.POPULATE:
      case CollectionAction.ALL:
        this.handlePopulateCollectionMessage(messageData.collection, messageData.data);
        break;
    }
  }

  private handlePopulateCollectionMessage(collection: string, content: any[]) {
    const collectionMap = this.getOrCreateCollectionMap(collection);
    collectionMap.clear();

    (content as any[]).forEach((entry) => {
      collectionMap.set(entry.id, entry);
    });

    this.updateAllElementSubject(collection);
    this.forceRefreshSubject.next(collection);
    this.forceRefreshSubject.next(null);
  }

  private updateAllElementSubject(collection: string) {
    const values = this.getCollection(collection);
    this.getOrCreateAllElementSubject(collection).next(values);
  }

  private getOrCreateAllElementSubject(collection: string): Subject<any[]> {
    const subject = this.allElementSubject.get(collection);
    if (!subject) {
      this.allElementSubject.set(collection, new BehaviorSubject(null));
    }
    return this.allElementSubject.get(collection);
  }

  private getOrCreateSingleElementSubject(collection: string): Subject<any> {
    const subject = this.singleElementSubject.get(collection);
    if (!subject) {
      this.singleElementSubject.set(collection, new BehaviorSubject(null));
    }
    return this.singleElementSubject.get(collection);
  }

  private getOrCreateCollectionMap(collection: string): Map<number, any> {
    const map = this.cacheMap.get(collection);
    if (!map) {
      this.cacheMap.set(collection, new Map<number, any>());
    }
    return this.cacheMap.get(collection);
  }
}
